import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Scale, History, Plus, Trash2, CalendarClock, Calculator, ExternalLink } from "lucide-react";
import { usePerms } from "@/hooks/usePerms";
import { formatValue, VersionValueEditor } from "@/components/admin/BaremeValueEditor";

const today = () => new Date().toISOString().slice(0, 10);
const eur = (n) => (n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "—");

export default function AdminBaremes() {
  const { can } = usePerms();
  const [at, setAt] = useState(today());
  const [data, setData] = useState(null);
  const [newVersion, setNewVersion] = useState(null); // { rule, form }
  const [history, setHistory] = useState(null); // { key, label, unit, versions }
  const [preview, setPreview] = useState(null);
  const [brut, setBrut] = useState(2500);

  const load = async (d = at) => {
    try { const { data: r } = await api.get(`/admin/regulatory/rules?date=${d}`); setData(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadPreview = async (d = at, b = brut) => {
    try { const { data: r } = await api.get(`/admin/regulatory/preview?date=${d}&brut=${b || 0}`); setPreview(r); } catch { setPreview(null); }
  };
  useEffect(() => { load(at); loadPreview(at, brut); }, [at]); // eslint-disable-line react-hooks/exhaustive-deps

  const byCategory = useMemo(() => {
    if (!data) return [];
    return data.categories.map((c) => ({ ...c, rules: data.rules.filter((r) => r.category === c.key) })).filter((c) => c.rules.length);
  }, [data]);

  const openNew = (rule) => setNewVersion({ rule, form: { value: rule.value, effective_from: "", source: "", source_url: "", notes: "" } });
  const openHistory = async (key) => {
    try { const { data: h } = await api.get(`/admin/regulatory/rules/${key}/history`); setHistory(h); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveVersion = async () => {
    const { rule, form } = newVersion;
    try {
      await api.post(`/admin/regulatory/rules/${rule.key}/versions`, form);
      toast.success(`Nouvelle version de « ${rule.label} » enregistrée — applicable dès le ${fmtDate(form.effective_from)}`);
      setNewVersion(null); load(); loadPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteVersion = async (v) => {
    if (!window.confirm(`Supprimer la version ${v.version} (effet ${fmtDate(v.effective_from)}) ?`)) return;
    try {
      await api.delete(`/admin/regulatory/versions/${v.id}`);
      toast.success("Version supprimée"); openHistory(v.key); load(); loadPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const canEdit = can("reglementaire", "creer");
  const canDelete = can("reglementaire", "supprimer");

  return (
    <div className="min-h-full" data-testid="admin-baremes-page">
      <PageHeader eyebrow="Regulatory Engine" title="Barèmes réglementaires versionnés"
        sub="PASS, SMIC, cotisations URSSAF / AGIRC-ARRCO, CSG-CRDS, taxes et barème IR sont stockés en base avec date d'effet, source et historique. Paie & Fiscal, simulateur de salaire et bulletins appliquent automatiquement la version en vigueur à la date du calcul — aucun redéploiement."
        right={
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Barème en vigueur au</Label>
              <Input type="date" value={at} onChange={(e) => e.target.value && setAt(e.target.value)} data-testid="baremes-date" className="h-10 w-[170px]" />
            </div>
          </div>
        } />

      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-[#0A0A0A] text-white p-5" data-testid="baremes-kpi-count">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#FFD700]"><Scale className="h-3 w-3" /> Paramètres</div>
              <div className="font-display text-2xl font-black mt-2">{data.meta.rules_count}</div>
              <div className="text-[10px] text-white/50 mt-1">résolus au {fmtDate(data.at)}</div>
            </div>
            <div className="bg-white border border-[#DEE2E6] p-5" data-testid="baremes-kpi-latest">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]"><CalendarClock className="h-3 w-3" /> Dernière entrée en vigueur</div>
              <div className="font-display text-2xl font-black mt-2">{fmtDate(data.meta.latest_effective_from)}</div>
              <div className="text-[10px] text-[#868E96] mt-1">{data.rules.filter((r) => r.upcoming).length} version(s) programmée(s) à venir</div>
            </div>
            <div className="bg-white border border-[#DEE2E6] p-5 md:col-span-2" data-testid="baremes-kpi-sources">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">Sources appliquées</div>
              <div className="text-xs text-[#495057] mt-2 space-y-0.5">{data.meta.sources.map((s) => <div key={s}>• {s}</div>)}</div>
            </div>
          </div>
        )}

        {/* Aperçu de calcul */}
        <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-5" data-testid="baremes-preview">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold"><Calculator className="h-4 w-4 text-[#002FA7]" /> Aperçu — salaire brut mensuel</div>
            <Input type="number" value={brut} onChange={(e) => setBrut(parseFloat(e.target.value) || 0)} onBlur={() => loadPreview(at, brut)} data-testid="baremes-preview-brut" className="h-9 w-[130px]" />
            <Button variant="outline" size="sm" onClick={() => loadPreview(at, brut)} data-testid="baremes-preview-run" className="h-9">Recalculer</Button>
          </div>
          {preview && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4 text-sm">
              <Mini label="Plafond SS" value={eur(preview.cotisations.plafond_periode)} testid="baremes-preview-plafond" />
              <Mini label="Part salariale" value={eur(preview.cotisations.total_salarial)} testid="baremes-preview-sal" />
              <Mini label="Part patronale" value={eur(preview.cotisations.total_patronal)} testid="baremes-preview-pat" />
              <Mini label="Net estimé" value={eur(preview.cotisations.net_estime)} testid="baremes-preview-net" />
              <Mini label="Coût employeur" value={eur(preview.cotisations.cout_employeur)} testid="baremes-preview-cout" />
            </div>
          )}
        </div>

        {byCategory.map((cat) => (
          <div key={cat.key} className="bg-white border border-[#DEE2E6]" data-testid={`baremes-cat-${cat.key}`}>
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#0A0A0A] text-white">{cat.label}</div>
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
              <div className="col-span-4">Paramètre</div><div className="col-span-3">Valeur en vigueur</div><div className="col-span-3">Depuis · Source</div><div className="col-span-2 text-right">Actions</div>
            </div>
            {cat.rules.map((r) => (
              <div key={r.key} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm" data-testid={`bareme-row-${r.key}`}>
                <div className="col-span-4">
                  <div className="font-semibold">{r.label}</div>
                  <div className="font-mono text-[11px] text-[#868E96]">{r.key} · {r.organisme}</div>
                </div>
                <div className="col-span-3 font-mono text-[13px]" data-testid={`bareme-value-${r.key}`}>{formatValue(r.unit, r.value)}</div>
                <div className="col-span-3 text-xs">
                  <div><span className="font-mono">{fmtDate(r.effective_from)}</span> <span className="text-[#868E96]">· v{r.version}{r.fallback ? " (repli)" : ""}</span></div>
                  <div className="text-[#868E96] truncate" title={r.source}>{r.source}</div>
                  {r.upcoming && <div className="text-[#002FA7] font-semibold" data-testid={`bareme-upcoming-${r.key}`}>→ v{r.upcoming.version} le {fmtDate(r.upcoming.effective_from)} : {formatValue(r.unit, r.upcoming.value)}</div>}
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => openHistory(r.key)} data-testid={`bareme-history-${r.key}`} className="h-8 px-2" title="Historique"><History className="h-3.5 w-3.5" /></Button>
                  {canEdit && <Button size="sm" onClick={() => openNew(r)} data-testid={`bareme-new-${r.key}`} className="h-8 px-2 bg-[#002FA7] hover:bg-[#002175] text-white" title="Nouvelle version"><Plus className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Nouvelle version */}
      <Dialog open={!!newVersion} onOpenChange={(o) => { if (!o) setNewVersion(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bareme-version-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">Nouvelle version — {newVersion?.rule.label}</DialogTitle></DialogHeader>
          {newVersion && (
            <div className="space-y-4">
              <div className="text-xs text-[#495057]">Valeur actuelle : <b className="font-mono">{formatValue(newVersion.rule.unit, newVersion.rule.value)}</b> (v{newVersion.rule.version}, depuis le {fmtDate(newVersion.rule.effective_from)}). La version précédente reste appliquée aux périodes antérieures à la date d'effet.</div>
              <VersionValueEditor unit={newVersion.rule.unit} value={newVersion.form.value} onChange={(v) => setNewVersion({ ...newVersion, form: { ...newVersion.form, value: v } })} />
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Date d'effet *</Label>
                  <Input type="date" value={newVersion.form.effective_from} onChange={(e) => setNewVersion({ ...newVersion, form: { ...newVersion.form, effective_from: e.target.value } })} data-testid="bareme-version-date" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Source *</Label>
                  <Input value={newVersion.form.source} placeholder="Arrêté du …, BOSS, circulaire URSSAF…" onChange={(e) => setNewVersion({ ...newVersion, form: { ...newVersion.form, source: e.target.value } })} data-testid="bareme-version-source" className="h-11" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Lien source (URL)</Label>
                  <Input value={newVersion.form.source_url} placeholder="https://" onChange={(e) => setNewVersion({ ...newVersion, form: { ...newVersion.form, source_url: e.target.value } })} data-testid="bareme-version-url" className="h-11" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Notes</Label>
                  <Textarea value={newVersion.form.notes} onChange={(e) => setNewVersion({ ...newVersion, form: { ...newVersion.form, notes: e.target.value } })} data-testid="bareme-version-notes" rows={2} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVersion(null)}>Annuler</Button>
            <Button onClick={saveVersion} disabled={!newVersion?.form.effective_from || !newVersion?.form.source.trim()} data-testid="bareme-version-save" className="bg-[#002FA7] hover:bg-[#002175] text-white">Publier la version</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historique */}
      <Sheet open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="bareme-history-sheet">
          <SheetHeader><SheetTitle className="font-display text-xl font-black">Historique — {history?.label}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3">
            {history?.versions.map((v) => {
              const current = v.effective_from <= at && (!v.effective_to || v.effective_to >= at);
              return (
                <div key={v.id} className={`border p-4 ${current ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6]"}`} data-testid={`bareme-hist-${v.version}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">Version {v.version}{current ? " · en vigueur" : ""}{v.seeded ? " · initiale" : ""}</div>
                      <div className="font-mono text-lg font-black mt-1">{formatValue(history.unit, v.value)}</div>
                      <div className="text-xs mt-1">Du <b>{fmtDate(v.effective_from)}</b> {v.effective_to ? <>au <b>{fmtDate(v.effective_to)}</b></> : "— sans fin"}</div>
                      <div className="text-xs text-[#495057] mt-1">{v.source} {v.source_url && <a href={v.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#002FA7] hover:underline ml-1"><ExternalLink className="h-3 w-3" />lien</a>}</div>
                      {v.notes && <div className="text-xs text-[#868E96] mt-1 italic">{v.notes}</div>}
                      <div className="text-[10px] text-[#868E96] mt-1">Créée le {new Date(v.created_at).toLocaleString("fr-FR")} par {v.created_by}</div>
                    </div>
                    {canDelete && history.versions.length > 1 && (
                      <Button size="sm" variant="outline" onClick={() => deleteVersion(v)} data-testid={`bareme-hist-delete-${v.version}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">{label}</div>
      <div className="font-display text-lg font-black mt-0.5">{value}</div>
    </div>
  );
}
